import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Paper,
  Badge,
  Divider,
  Box,
  CircularProgress
} from '@material-ui/core';
import {
  setSelectedValuation,
  setTransientValuationInputs,
  setTransientValuationType,
  setTransientValuationNotes,
  savePostAcqUW,
  appNotify,
  setTransientValuation,
  setValuationTypeModalOptions,
} from 'store/actions';
import {
  isUsersValuationSelector,
  selectedProformaInputsSelector,
  nextTaskSelector,
} from 'store/selectors';
import {
  PropertyUWProformaInput
} from '../propertyUnderwrite';
import { sendClientLog } from 'services/logService';
import { NoteModal } from 'components/underwrite/shared';
import ValuationTypeModal from '../ValuationTypeModal';
import { Constants } from 'utilities/constants';
import classNames from 'classnames';
import { GetValuationTypeMetadata } from "api/revue/underwriteData";

const PostAcqUWProforma = ({
  classes,
  user,
  userHasUnderwriteReadOnlyRole,
  selectedOpportunity,
  selectedContainer,
  selectedProformaInputs,
  valuations,
  selectedValuation,
  transientValuation,
  valuationIsDirty,
  isUsersValuation,
  nextTask,
  compsLoadingState,
  isSavingUW,
  valuationTypeModalOptions,
  viewUnderwriteModalIsOpen,
  handleModalClose,
  setSelectedValuation,
  setTransientValuation,
  setTransientValuationInputs,
  setTransientValuationNotes,
  setTransientValuationType,
  setValuationTypeModalOptions,
  savePostAcqUW,
  appNotify,
  history
}) => {
  const [proformaValuation, setProformaValuation] = useState(selectedValuation) // This state variable stores the transient valution/selected valuation (whatever is shown/editable on screen)
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isValuationTypesLoading, setIsValuationTypesLoading] = useState(false);
  const [hasInputLimitError, setHasInputLimitError] = useState({ rent: false, afterRepairValue: false });

  useEffect(() => {
    getValuationTypes();
  }, []);

  useEffect(() => { /**Trigger when transient valuation updates */
    if (isUsersValuation) {
      setProformaValuation(transientValuation);
    }
  }, [transientValuation]);

  useEffect(() => { /**Triggers when using dropdown to select user valuation. Show transient values for user's own valuation */
    const valuation = isUsersValuation ? transientValuation : selectedValuation;
    setProformaValuation(valuation);
  }, [selectedValuation]);

  const getValuationTypes = async () => {
    try {
      setIsValuationTypesLoading(true);
      const { possibleValues, defaultValue } = await GetValuationTypeMetadata(selectedOpportunity.id);

      setValuationTypeModalOptions({ possibleValues, defaultValue });
      setIsValuationTypesLoading(false);
    } catch (e) {
      console.log(e);
      appNotify('Failed to fetch valuation types', 'error');
      setIsValuationTypesLoading(false);
    }
  }

  /**Triggers when user chooses valuation from dropdown */
  const onValuationIndexChange = e => {
    const newSelectedValuation = valuations[e.target.value];
    setSelectedValuation(newSelectedValuation);
  }

  /**Triggers when user click 'Take Valuation' */
  const takeUWClick = () => {
    // create new transient valuation object but with other person's valuation
    const newTransientValuation = {
      ...selectedValuation,
      createdOn: transientValuation.createdOn,
      id: transientValuation.id,
      user: transientValuation.user
    }

    setSelectedValuation(valuations.find(x => x.user.id === user.id), false);      // set back to user's valuation
    setTransientValuation(newTransientValuation);
    appNotify(`Took ${selectedValuation.user.fullName}'s Underwrite`, 'success', { autoHideDuration: 15000000 });
  }

  /**This gets passed into 'PropertyUWProformaInput' component as change handler */
  const handlePropertyUWProformaInputChange = (newInput) => {
    if (isUsersValuation) {
      let newValuationInput = { ...proformaValuation.valuationInput };
      newValuationInput[newInput.inputGroupName] = newInput.selectedRadioValue;
      newValuationInput[newInput.inputGroupName + 'Type'] = newInput.selectedRadioType;

      setTransientValuationInputs(newValuationInput, true); // Pass true to skip setting shouldCalculateProforma flag
    }
  }

  /**This gets passed into 'PropertyUWProformaInput' component to handle error*/
  const handleInputError = (errorMsg) => {
    appNotify(errorMsg, 'warning');
  }

  const setInputLimitError = (inputGroup, hasError) => { 
    setHasInputLimitError({ 
      ...hasInputLimitError, 
      [inputGroup]: hasError 
    }); 
  } 

  const onValuationTypeChange = (e) => {
    setTransientValuationType(e.target.value);
  }

  /**Open notes modal */
  const onClickNoteSection = () => {
    setIsNoteModalOpen(true);
  }

  const onCancelNotes = () => {
    setIsNoteModalOpen(false);
  }

  const onUpdateNotes = (notesValue) => {
    const notes = notesValue !== '' ? notesValue : null
    if (isUsersValuation) {
      setTransientValuationNotes(notes);
    }
  }

  const hasRequiredValues = () => {
    const { valuationInput, type } = transientValuation;
    return (valuationInput.rent != null || valuationInput.afterRepairValue != null) && (type || valuationTypeModalOptions.defaultValue);
  }

  const saveUWClick = async () => {
    if (!hasRequiredValues()) {
      appNotify('Cannot save with missing required values', 'error');
      return;
    }

    sendClientLog('Info', `${viewUnderwriteModalIsOpen ? 'Explore' : 'Post-Acq UW'} - User clicked Save on proforma. txnOpp ${selectedOpportunity.id}`);

    const saveUWPayload = {
      defaultValue: valuationTypeModalOptions?.defaultValue,
      proformaInputsContainerId: selectedProformaInputs.id,
      transientValuation
    }
    savePostAcqUW(saveUWPayload);
  }

  const canSave = valuationIsDirty || transientValuation?.id === 0; // Valuation can be saved if persisted valuation is modified, or if no persisted valuation exists (transient id of 0)
  const saveAndNavigateToNextTask = async () => {
    const buttonType = canSave ? 'Save and Next' : 'Next';
    sendClientLog('Info', `Post-Acq UW - User clicked ${buttonType} on proforma. txnOpp ${selectedOpportunity.id}`);

    const nextTaskId = nextTask ? nextTask.id : null
    if (!canSave) {
      // just navigate to next if nothing needs to be saved.
      navigateToNextOpportunity(nextTaskId)
      return
    }

    if (!hasRequiredValues()) {
      appNotify('Cannot save with missing required values', 'error');
      return;
    }

    const saveUWPayload = {
      defaultValue: valuationTypeModalOptions?.defaultValue,
      proformaInputsContainerId: selectedProformaInputs.id,
      transientValuation,
      history,
      nextTaskId
    }
    savePostAcqUW(saveUWPayload)
  }

  const navigateToNextOpportunity = (navigateToId) => {
    if (navigateToId) {
      history.push(`${Constants.Routes.UNDERWRITE_PROPERTY}/${navigateToId}`);
    }
  }

  const saveAndCloseModal = () => {
    const buttonType = canSave ? 'Save and Close' : 'Close';
    sendClientLog('Info', `Explore - User clicked ${buttonType} on proforma. txnOpp ${selectedOpportunity.id}`);

    if (!canSave) {
      // just navigate to next if nothing needs to be saved.
      handleModalClose();
      return
    }

    if (!hasRequiredValues()) {
      appNotify('Cannot save with missing required values', 'error');
      return;
    }

    const saveUWPayload = {
      defaultValue: valuationTypeModalOptions?.defaultValue,
      proformaInputsContainerId: selectedProformaInputs.id,
      transientValuation,
      history
    }
    savePostAcqUW(saveUWPayload)
    handleModalClose();
  }

  const canEdit = isUsersValuation && !selectedContainer?.viewingReadonlyMode;
  const foundSelectedValuationIndex = valuations.findIndex(
    val => selectedValuation ? val.id === selectedValuation.id : false
  );
  const isWithinValidBedBathRange = selectedOpportunity.beds <= 10 && selectedOpportunity.baths <= 10; // AT-2514 Prevent Saving and Submitting if bed/bath > 10
  const isMissingValuationType = valuationTypeModalOptions?.possibleValues && !valuationTypeModalOptions.defaultValue && !transientValuation.type;
  const someInputLimitError = Object.keys(hasInputLimitError).some(key => hasInputLimitError[key] === true);

  return (
    <>
      {transientValuation && selectedValuation &&
        <div className={classes.container}>
          <div className={classes.header}>
            <div className={classes.headerText}>
              <Typography variant='subtitle2' className={classes.proformaHeader}>Underwrite</Typography>
              {/* {selectedOpportunity.underwriteStatus &&
                <UnderwriteStatusChip underwriteStatus={selectedOpportunity.underwriteStatus} style={{ marginLeft: 8 }} />
              } */}
            </div>
            <Divider />
          </div>
          <div className={classNames(classes.inputsContainer, selectedContainer?.viewingReadonlyMode && classes.grayBackground)}>
            {valuations.length > 0 && proformaValuation &&
              <>
                <div className={classes.inputs}>
                  <div className={classes.userSelectHeader}>
                    <div className={classes.userSelectLabel}>
                      <Badge badgeContent={valuations.length} color='primary' classes={{ badge: classes.userBadge }} overlap='rectangular'>
                        <Typography variant='body1' className={classes.headers}>Underwrites</Typography>
                      </Badge>
                    </div>
                    {/* Empty div to space icons to the end and keep header centered */}
                    <div></div>
                    <FormControl>
                      <Select
                        value={foundSelectedValuationIndex > -1 ? foundSelectedValuationIndex : ''}
                        onChange={onValuationIndexChange}
                        inputProps={{ name: 'selectedValuationIndex', id: 'valuationsDropdown' }}
                        classes={{ root: classes.userSelectRoot }}
                      >
                        {valuations.map((el, index) => (
                            <MenuItem key={el.id} value={index}>
                              <span
                                style={
                                  el.id === 0
                                    ? {
                                        outline: '1px dotted gray',
                                        padding: '0 2px',
                                        display: 'inline-block',
                                      }
                                    : undefined
                                }
                              >
                                {el.user.fullName
                                  ? el.user.fullName.toUpperCase()
                                  : el.user.name.toUpperCase()}
                              </span>
                            </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  {!isUsersValuation
                    ? <Paper className={classes.otherUserWarning}>
                      <Typography variant='subtitle2'>You are viewing another user's underwrite.</Typography>
                      {!selectedContainer?.viewingReadonlyMode ? <Typography variant='caption' className={classes.linkStyle} onClick={takeUWClick}>Take Underwrite</Typography> : null}
                    </Paper>
                    : null
                  }

                  {/* Valuation Inputs */}
                  <div className={classes.inputsHeader}>
                    <Typography variant='caption' color='primary' className={classes.headers}>Inputs</Typography>
                    {/* Empty div to space icons to the end and keep header centered */}
                    <div></div>
                  </div>
                  <div>
                    <PropertyUWProformaInput
                      inputGroup='rent'
                      inputSubheader='Rent'
                      inputValue={proformaValuation.valuationInput.rent}
                      inputValueType={proformaValuation.valuationInput.rentType}
                      onInputChange={handlePropertyUWProformaInputChange}
                      onKeyDown={() => {}}
                      onError={handleInputError}
                      setInputLimitError={setInputLimitError} 
                      radioValuesArray={selectedProformaInputs ? selectedProformaInputs.rents : []}
                      ancillary={{ sqft: selectedOpportunity?.sqft }}
                      isDisabled={!canEdit}
                      isLoading={compsLoadingState.isLoading}
                      required={proformaValuation.valuationInput.afterRepairValue == null}
                      isPostAcquisitionUnderwrite={true} />
                    <PropertyUWProformaInput
                      inputGroup='afterRepairValue'
                      inputSubheader='After Repair Value'
                      inputValue={proformaValuation.valuationInput.afterRepairValue}
                      inputValueType={proformaValuation.valuationInput.afterRepairValueType}
                      onInputChange={handlePropertyUWProformaInputChange}
                      onKeyDown={() => {}}
                      onError={handleInputError}
                      setInputLimitError={setInputLimitError} 
                      radioValuesArray={selectedProformaInputs ? selectedProformaInputs.afterRepairValues : []}
                      ancillary={{ sqft: selectedOpportunity?.sqft }}
                      isDisabled={!canEdit}
                      isLoading={compsLoadingState.isLoading}
                      required={proformaValuation.valuationInput.rent == null}
                      isPostAcquisitionUnderwrite={true} />
                    <div>
                      <div className={classes.inputTitle}>
                        <Typography variant='body2' className={classNames(isMissingValuationType ? classes.inputsSubheader : classes.inputsSubheaderSelected)}>
                          Valuation Type
                        </Typography>
                        {isValuationTypesLoading &&
                          <CircularProgress size={15} />
                        }
                      </div>
                      <FormControl fullWidth>
                        <Select
                          value={(!isUsersValuation ? selectedValuation.type : transientValuation.type) ?? valuationTypeModalOptions?.defaultValue ?? ''}
                          onChange={onValuationTypeChange}
                          inputProps={{ name: 'valuationType', id: 'valuationTypeDropdown' }}
                          classes={{ root: classes.userSelectRoot }}
                        >
                          {valuationTypeModalOptions?.possibleValues.map((el, index) => (
                            <MenuItem key={el} value={el}>{el}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                  <Divider style={{ margin: '8px 0' }}/>
                  {/* Notes Input   */}
                  <div className={classes.noteContainer}>
                    <Box borderBottom={1} borderColor="grey" className={classes.noteSection} onClick={onClickNoteSection} disabled={!canEdit}>
                      {proformaValuation.notes
                        ?
                        <Typography className={classes.noteContent} display='block' noWrap>
                          {proformaValuation.notes}
                        </Typography>
                        :
                        <Typography className={classes.notePlaceholder} display='block' noWrap>
                          Take a note...
                        </Typography>}
                    </Box>
                  </div>
                  {/* Buttons */}
                  <div className={classes.buttonContainer}>
                    <Button
                      className={classes.halfWidthButton}
                      disabled={!canEdit || isSavingUW || !canSave || selectedOpportunity?.isProcessing || !isWithinValidBedBathRange || userHasUnderwriteReadOnlyRole || someInputLimitError}
                      variant='contained'
                      color='primary'
                      onClick={saveUWClick}
                      tabIndex={-1}>
                      {isSavingUW ?
                        <CircularProgress size={24} color='inherit' />
                        : 'Save'}</Button>
                    {viewUnderwriteModalIsOpen 
                      ? <Button
                          className={classes.halfWidthButton}
                          disabled={canSave ? (selectedOpportunity?.isProcessing || !canEdit || isSavingUW || !isWithinValidBedBathRange || userHasUnderwriteReadOnlyRole || someInputLimitError) : isSavingUW}
                          variant={canSave ? 'contained' : 'outlined'}
                          color='primary'
                          onClick={saveAndCloseModal}
                          tabIndex={-1}>
                          {isSavingUW ?
                            <CircularProgress size={24} color='inherit' /> :
                            canSave ?
                              'Save & Exit'
                              : 'Exit'}</Button>
                      : <Button
                          className={classes.halfWidthButton}
                          disabled={canSave ? (selectedOpportunity?.isProcessing || !canEdit || isSavingUW || !nextTask || !isWithinValidBedBathRange || userHasUnderwriteReadOnlyRole || someInputLimitError) : (!nextTask || isSavingUW)}
                          variant='contained'
                          color='primary'
                          onClick={saveAndNavigateToNextTask}
                          tabIndex={-1}>
                          {isSavingUW ?
                            <CircularProgress size={24} color='inherit' /> :
                            canSave ?
                              'Save & Next'
                              : 'Next'}</Button>
                    }
                  </div>
                </div>
              </>
            }
          </div>
          <ValuationTypeModal />
          <NoteModal
            isModalOpen={isNoteModalOpen}
            onCancel={onCancelNotes}
            onUpdate={onUpdateNotes}
            value={proformaValuation?.notes}
            isReadOnly={!canEdit}
            isPostAcquisitionUnderwrite={true}
          />
        </div>
      }
    </>
  )
}

const mapStateToProps = state => {
  const selectedProformaInputs = selectedProformaInputsSelector(state);
  const valuations = selectedProformaInputs?.valuations || [];
  const userHasUnderwriteReadOnlyRole = state.appState.user.roles.underwriteReadOnly

  return {
    userHasUnderwriteReadOnlyRole,
    selectedOpportunity: state.propertyUWState.propertyUWClientTxnOpp,
    selectedContainer: state.propertyUWState.selectedContainer,
    selectedProformaInputs,
    valuations,
    selectedValuation: state.propertyUWState.selectedValuation,
    isUsersValuation: isUsersValuationSelector(state),
    transientValuation: state.propertyUWState.transientValuation,
    valuationIsDirty: state.propertyUWState.valuationIsDirty,
    user: state.appState.user,
    isSavingUW: state.propertyUWState.isSavingUW,
    compsLoadingState: state.propertyUWState.compsLoadingState,
    nextTask: nextTaskSelector(state, Constants.Module.UNDERWRITE),
    valuationTypeModalOptions: state.propertyUWState.valuationTypeModalOptions
  };
}

const mapDispatchToProps = {
  setSelectedValuation,
  setTransientValuationInputs,
  setTransientValuationType,
  setTransientValuationNotes,
  setTransientValuation,
  savePostAcqUW,
  appNotify,
  setValuationTypeModalOptions
};

const styles = theme => ({
  container: {
    gridArea: 'proforma',
    height: '100%',
    backgroundColor: '#fff',
    borderLeft: '2px solid rgba(0, 0, 0, 0.12)',
    paddingBottom: '10px',
    display: 'flex',
    flexDirection: 'column'
  },
  inputsContainer: {
    overflowY: 'auto',
  },
  headers: {
    fontWeight: 'bold',
    fontSize: '14px',
    alignSelf: 'center'
  },
  inputs: {
    marginLeft: '5px',
    marginRight: '5px',
  },
  userSelectHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    marginTop: theme.spacing(0.5)
  },
  userSelectLabel: {
    display: 'flex'
  },
  inputsHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    marginTop: theme.spacing(0.5)
  },
  proforma: {
    gridArea: 'proforma',
    paddingLeft: '5px',
    paddingRight: '5px',
    marginTop: theme.spacing(0.5)
  },
  header: {
    gridArea: 'expander',
    backgroundColor: '#fff',
    width: '100%',
    top: '0',
    zIndex: '1',
  },
  headerText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  noteContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '10px 0 10px 0',
  },
  noteSection: {
    width: '90%',
    cursor: 'pointer',
    padding: '5px'
  },
  noteContent: {
    width: '100%',
    height: '100%'
  },
  notePlaceholder: {
    color: 'gray'
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(1)
  },
  halfWidthButton: {
    flex: '0 0 calc(45% - 4px)',
    margin: '0 4px'
  },
  proformaHeader: {
    color: 'black',
    fontSize: '1rem',
  },
  otherUserWarning: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: `${theme.spacing(2)}px ${theme.spacing(1)}px`,
    textAlign: 'center',
    padding: `${theme.spacing(1)}px 0`,
    backgroundColor: theme.palette.warning.main,
  },
  userSelectRoot: {
    marginLeft: theme.spacing(0.5)
  },
  userBadge: {
    top: 10,
    right: -16
  },
  linkStyle: {
    cursor: 'pointer',
    textDecoration: 'underline',
    color: '#0000EE',
  },
  grayBackground: {
    filter: 'grayscale(0.8) brightness(0.85) opacity(0.9)',
    backgroundColor: '#fff'
  },
  inputTitle: {
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'center',
    paddingTop: '5px',
  },
  inputsSubheader: {
    marginRight: 8,
    fontWeight: 'bold',
    color: theme.palette.red.main,
    fontSize: '12px',
  },
  inputsSubheaderSelected: {
    marginRight: 8,
    fontWeight: 'bold',
    fontSize: '12px',
  },
});

export default compose(
  withRouter,
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(PostAcqUWProforma);